import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '@environment';
import { CognitoAuthService } from '@commons/services/cognito-auth/cognito-auth.service';
import { FunctionsService } from '../../services/functions/functions.service';
import {
  AssignGradesGrants,
  BidDetailGrants,
  BidDevicePriceGrants,
  BidInboundGrants,
  BidOutboundGrants,
  BidsGrants,
  DockReceiptGrants,
  DockReceiptImageGrants,
  DropShipmentGrants,
  isAuthorized,
  PoSummaryGrants,
  PrepLogGrants,
  ReceivePoGrants,
  SerialInGrants,
  SoDispatcherGrants,
  TradeInDetailGrants,
  TradeInGrants,
  TradeInDevicePriceGrants,
  LocationPrefixesGrants,
  ItemLocationsGrants,
  DeviceAuditGrants,
  BidCostsGrants,
  BidLocationsGrants,
  TradeInLocationsGrants,
  DeviceQueryScreenGrants,
  CategoryMappingGrants,
  LobsGrants,
  LobsDetailGrants,
  ShipFromWarehouseGrants,
  FileUploadGrants,
  ReportsGrants,
  ReportsDesignerGrants,
  DashboardGrants,
  UserManagementGrants,
  UserManagementDetailGrants,
  UserManagementActivityGrants,
} from 'security-grants';
import { RoutePermissions } from '../route-permissions';
import { saveUserActivity } from '@commons/utils';

/**
 * PermissionsService class.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  /**
   * Creates an instance of the component.
   * @param router - The Angular router.
   * @param functionsService - The functions service service instance.
   * @param httpClient - The http client value.
   */
  constructor(
    private router: Router,
    private functionsService: FunctionsService,
    private httpClient: HttpClient,
    private authService: CognitoAuthService,
  ) {}

  /**
   * Gets grants for the currently selected LOB from the JWT access token.
   * The token is already enriched by preTokenRedis with per-LOB grants.
   * @returns {Promise<string[]>} grants for the selected LOB
   */
  async getGrants(): Promise<string[]> {
    try {
      const { claims } = await this.authService.currentSession();
      const lobs = claims['lobs'] ?? [];
      const selectedLob = this.functionsService.getLobSelected();

      const currentLob = lobs.find((lob: any) => lob.lob_name === selectedLob);
      return currentLob?.grants ?? [];
    } catch (error) {
      return [];
    }
  }

  /**
   * The function isAuthorizedGrant checks if a user has authorization for a specific grant based on
   * their user grants.
   * @param {string[]} userGrants - The `userGrants` parameter is an array of strings representing the
   * grants that a user has been assigned.
   * @param { AssignGradesGrants | BidsGrants | BidDetailGrants | DockReceiptGrants | DropShipmentGrants | PoSummaryGrants | PrepLogGrants | ReceivePoGrants | SerialInGrants | SoDispatcherGrants | TradeInGrants | TradeInDetailGrants | TradeInDevicePriceGrants | DockReceiptImageGrants | BidOutboundGrants | BidInboundGrants | BidDevicePriceGrants | LocationPrefixesGrants | ItemLocationsGrants | DeviceAuditGrants | BidCostsGrants | BidLocationsGrants | TradeInLocationsGrants | DeviceQueryScreenGrants | CategoryMappingGrants | ShipFromWarehouseGrants | ReportsGrants | ReportsDesignerGrants} grant
   * 'security-grants' library permissions
   * @returns {boolean} The operation result.
   */
  isAuthorizedGrant(
    userGrants: string[],
    grant:
      | AssignGradesGrants
      | BidsGrants
      | BidDetailGrants
      | BidDevicePriceGrants
      | BidInboundGrants
      | BidOutboundGrants
      | DockReceiptGrants
      | DockReceiptImageGrants
      | DropShipmentGrants
      | PoSummaryGrants
      | PrepLogGrants
      | ReceivePoGrants
      | SerialInGrants
      | SoDispatcherGrants
      | TradeInDetailGrants
      | TradeInGrants
      | TradeInDevicePriceGrants
      | LocationPrefixesGrants
      | ItemLocationsGrants
      | DeviceAuditGrants
      | BidCostsGrants
      | BidLocationsGrants
      | TradeInLocationsGrants
      | DeviceQueryScreenGrants
      | CategoryMappingGrants
      | LobsGrants
      | LobsDetailGrants
      | ShipFromWarehouseGrants
      | FileUploadGrants
      | ReportsGrants
      | ReportsDesignerGrants
      | DashboardGrants
      | UserManagementGrants
      | UserManagementDetailGrants
      | UserManagementActivityGrants,
  ): boolean {
    return isAuthorized(userGrants, grant);
  }

  /**
   * The function `canActivateRoute` Check if the user can access that route
   * @param {string} [routePath] - The `routePath` parameter
   * @returns {Promise<boolean>} The operation result.
   */
  async canActivateRoute(routePath: string = ''): Promise<boolean> {
    try {
      const userGrants = await this.getGrants();

      const screen = RoutePermissions[routePath];

      if (!screen) return true;

      const isAuthorizedValue = isAuthorized(userGrants, screen);
      saveUserActivity(this.httpClient, screen, isAuthorizedValue);
      if (!isAuthorizedValue) this.router.navigate(['home']);

      return isAuthorizedValue;
    } catch (_error) {
      return false;
    }
  }

  /**
   * The function `isAuthorizedByGrant` Check if the user can access by grant
   * @param { AssignGradesGrants | BidsGrants | BidDetailGrants | DockReceiptGrants | DropShipmentGrants | PoSummaryGrants | PrepLogGrants | ReceivePoGrants | SerialInGrants | SoDispatcherGrants | TradeInGrants | TradeInDetailGrants | TradeInDevicePriceGrants | DockReceiptImageGrants | BidOutboundGrants | BidInboundGrants | BidDevicePriceGrants | LocationPrefixesGrants | ItemLocationsGrants | DeviceAuditGrants | BidCostsGrants | BidLocationsGrants | TradeInLocationsGrants | DeviceQueryScreenGrants | CategoryMappingGrants | LobsGrants | LobsDetailGrants | ShipFromWarehouseGrants | FileUploadGrants} grant
   * 'security-grants' library permissions
   * @returns {Promise<boolean>} The operation result.
   */
  async isAuthorizedByGrant(
    grant:
      | AssignGradesGrants
      | BidsGrants
      | BidDetailGrants
      | BidDevicePriceGrants
      | BidInboundGrants
      | BidOutboundGrants
      | DockReceiptGrants
      | DockReceiptImageGrants
      | DockReceiptGrants
      | DockReceiptImageGrants
      | DropShipmentGrants
      | PoSummaryGrants
      | PrepLogGrants
      | ReceivePoGrants
      | SerialInGrants
      | SoDispatcherGrants
      | TradeInDetailGrants
      | TradeInGrants
      | TradeInDevicePriceGrants
      | LocationPrefixesGrants
      | ItemLocationsGrants
      | DeviceAuditGrants
      | BidCostsGrants
      | BidLocationsGrants
      | TradeInLocationsGrants
      | DeviceQueryScreenGrants
      | CategoryMappingGrants
      | LobsGrants
      | LobsDetailGrants
      | ShipFromWarehouseGrants
      | FileUploadGrants
      | ReportsGrants
      | ReportsDesignerGrants
      | DashboardGrants
      | UserManagementGrants
      | UserManagementDetailGrants
      | UserManagementActivityGrants,
  ): Promise<boolean> {
    try {
      const userGrants = await this.getGrants();

      return this.isAuthorizedGrant(userGrants, grant);
    } catch (_error) {
      return false;
    }
  }
}

/**
 * The function `canActivatePermission` checks if a user has permission to access a specific route
 * based on the route's path.
 * @param {ActivatedRouteSnapshot} route - The `route` parameter
 * @returns The operation result.
 */
export const canActivatePermission: CanActivateFn = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(PermissionsService).canActivateRoute(route.routeConfig?.path);
};
